import {
  Alert,
  Button,
  Form,
  Input,
  Typography
} from 'antd';
import { useState } from 'react';
import {
  generateRecoveryCodes,
  messageFor,
  statusOf
} from '../../../service/mfa.service';
import { SESSION_EXPIRED } from './mfaErrors';

/**
 * The re-authentication in front of generating recovery codes, shared by the settings card's
 * Regenerate button and the blocking codes popup.
 *
 * It asks for the password **even though the caller already holds a valid token**, because
 * this route mints a lasting way past the second factor: a stolen session must not be able to
 * walk off with ten codes that survive the owner changing their password.
 *
 * `otp` is mandatory on an account with an authenticator, which is why `hasTotp` is a required
 * prop rather than something guessed. It comes from `GET /users/me/mfa`, whose `totp` is read
 * live from Keycloak. Omitting the field on such an account answers 400, and that 400 is a
 * *different* failure from the 403 that a wrong password gives; they must not read alike.
 */

interface RecoveryCodesReauthProps {
  /** From `GET /users/me/mfa`. When true the authenticator code is required. */
  hasTotp: boolean;
  onGenerated: (codes: string[]) => void;
  onCancel?: () => void;
  submitLabel?: string;
  /** Set on the regenerate path, where an existing set is about to be destroyed. */
  isRegenerating?: boolean;
}

interface FormValues {
  password: string;
  otp?: string;
}

const RecoveryCodesReauth = ({
  hasTotp,
  onGenerated,
  onCancel,
  submitLabel = 'Generate codes',
  isRegenerating = false
}: RecoveryCodesReauthProps) => {
  const [form] = Form.useForm<FormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (values: FormValues) => {
    setIsSubmitting(true);
    setError(null);

    let codes: string[] | undefined;
    try {
      const { data } = await generateRecoveryCodes(
        values.password,
        hasTotp ? values.otp : undefined
      );
      codes = data?.recovery_codes;
    } catch (err: any) {
      setError(
        messageFor(err, {
          // The server's own wording is preferred for these; they are the fallbacks if it
          // sends none.
          400: 'Enter the code from your authenticator app as well.',
          401: SESSION_EXPIRED,
          403: 'Those credentials are not correct.',
          429: 'Too many attempts. Please wait a minute and try again.'
        })
          // An authenticator code cannot be spent twice. Signing in with one and then
          // reusing it here inside the same 30-second window is refused as a replay, and
          // "not correct" is a baffling thing to read about a code you just watched work.
          + (hasTotp && statusOf(err) === 403
            ? ' If you just signed in with that authenticator code, wait for your app to show'
              + ' a new one: each code can only be used once.'
            : '')
      );
      return;
    } finally {
      setIsSubmitting(false);
    }

    // Deliberately outside the try. The server has already retired the old set by this
    // point, so if handing the new one to the parent throws, that must surface as itself
    // rather than being caught above and mis-reported as a credentials problem, which
    // would leave the user believing nothing happened when their codes had in fact changed.
    if (!codes?.length) {
      setError(
        'Your codes were replaced, but the new ones did not come back. Generate another set '
        + 'and save those: any codes you had written down no longer work.'
      );
      return;
    }
    onGenerated(codes);
  };

  return (
    <div>
      {isRegenerating && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message={
            <span className="text-[13px]">
              Generating a new set <strong>retires your current codes immediately</strong>.
              Anyone holding the old printout will be holding dead codes.
            </span>
          }
        />
      )}

      {error && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={error}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Typography.Paragraph type="secondary" className="text-[13px]">
        Confirm it is you. These codes get past your second factor, so a signed-in session on
        its own is not enough to mint them.
      </Typography.Paragraph>

      {/* `component={false}` renders the fields without a `<form>` element of its own.
          The Security tab sits inside the profile page's Formik `<form>`, and nesting one
          form in another is invalid HTML whose submit handling browsers disagree about,
          so the submit below is driven through the form instance instead of a DOM submit
          event, and nothing this panel does can reach the profile form. */}
      <Form<FormValues>
        form={form}
        component={false}
        layout="vertical"
        onFinish={submit}
        requiredMark={false}
      >
        <Form.Item
          label="Your password"
          name="password"
          rules={[{ required: true, message: 'Enter your password.' }]}
        >
          <Input.Password size="large" autoComplete="current-password" />
        </Form.Item>

        {hasTotp && (
          <Form.Item
            label="Code from your authenticator app"
            name="otp"
            rules={[
              { 
                required: true, 
                message: 'Enter the code from your authenticator app.' 
              }, { pattern: /^\d{6}$/, message: 'That should be six digits.' }
            ]}
          >
            <Input
              size="large"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="000000"
            />
          </Form.Item>
        )}

        <div className="flex items-center justify-end gap-2">
          {onCancel && (
            <Button onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            loading={isSubmitting}
            danger={isRegenerating}
            onClick={() => form.submit()}
          >
            {submitLabel}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default RecoveryCodesReauth;
